/**
 * Game events, sent to the GA4 property already loaded in index.html.
 *
 * Consent mode starts at `analytics_storage: 'denied'` and is only granted
 * once the player accepts (see ads.js). Google drops events raised before
 * that rather than queueing them, which is the behaviour we want — but it
 * does mean every count here covers consenting players only. Read these as
 * ratios between themselves, never as absolute player totals.
 *
 * Never throws and never blocks: analytics failing is not a reason for the
 * game to stop working.
 */

/**
 * Mirrors what ads.js has told Google. Set from there and nowhere else, so it
 * can never claim a consent GA has not been given — which is what makes it
 * safe to use for deciding whether an event actually counted.
 */
let consentGranted = false;

/** Once-per-day events raised before consent, waiting to be counted. */
const heldForConsent = [];

export function track(name, params = {}) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }
  } catch (e) {
    // Deliberately swallowed.
  }
}

const storageKeyFor = (key) => `angle.tracked.${key}`;

function readSent(key) {
  try {
    return localStorage.getItem(storageKeyFor(key));
  } catch (e) {
    return null;
  }
}

function writeSent(key, dayString) {
  try {
    localStorage.setItem(storageKeyFor(key), dayString);
  } catch (e) {}
}

/**
 * Fire an event at most once for a given day.
 *
 * A finished round is restored from storage as readily as it is played, so
 * reporting it from a plain effect would count every reload by a player who
 * had already finished. Keyed this way the events count players per day, which
 * is what makes round 1 and round 2 comparable to each other.
 *
 * The day is only marked once consent is known to have been given. Marking it
 * first meant a player who finished a round while the consent dialog was still
 * up had the event dropped by Google *and* the day recorded as reported, so it
 * was never sent at all. Held instead, and flushed the moment consent lands;
 * if it never lands the day stays unmarked and the next visit tries again.
 */
export function trackOncePerDay(key, dayString, name, params = {}) {
  if (readSent(key) === dayString) return;

  track(name, params);

  if (consentGranted) {
    writeSent(key, dayString);
    return;
  }
  if (!heldForConsent.some((held) => held.key === key)) {
    heldForConsent.push({ key, dayString, name, params });
  }
}

/**
 * Called by ads.js once Google has been told consent is granted. Anything
 * raised before then is sent again — the first copy was dropped, so there is
 * nothing to double-count.
 */
export function markAnalyticsConsent() {
  consentGranted = true;
  const waiting = heldForConsent.splice(0, heldForConsent.length);
  for (const held of waiting) {
    if (readSent(held.key) === held.dayString) continue;
    track(held.name, held.params);
    writeSent(held.key, held.dayString);
  }
}
